/**
 * Google Tag Manager (GTM) and optional gtag (e.g. Google Ads AW-xxx).
 * GTM loads when REACT_APP_GTM_ID is set; gtag loads when REACT_APP_GTAG_AW_ID is set.
 * Pushes virtual page views to dataLayer when the app tab changes.
 */
import React, { useEffect, useRef } from 'react';

const GTM_ID = process.env.REACT_APP_GTM_ID;
const GTAG_AW_ID = process.env.REACT_APP_GTAG_AW_ID;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export interface GoogleAnalyticsProps {
  /** Current page/tab id – when this changes, a virtual page_view is pushed to dataLayer */
  page?: string;
}

export const GoogleAnalytics: React.FC<GoogleAnalyticsProps> = ({ page }) => {
  const loaded = useRef(false);
  const lastPage = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!GTM_ID || typeof window === 'undefined') return;

    if (!loaded.current) {
      loaded.current = true;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js'
      });

      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
      document.head.appendChild(script);

      const noscript = document.createElement('noscript');
      const iframe = document.createElement('iframe');
      iframe.src = `https://www.googletagmanager.com/ns.html?id=${GTM_ID}`;
      iframe.height = '0';
      iframe.width = '0';
      iframe.style.display = 'none';
      iframe.style.visibility = 'hidden';
      noscript.appendChild(iframe);
      document.body.insertBefore(noscript, document.body.firstChild);
    }
  }, []);

  // Google tag (gtag.js) e.g. for Ads (AW-xxx) – loads when REACT_APP_GTAG_AW_ID is set
  useEffect(() => {
    if (!GTAG_AW_ID || typeof window === 'undefined') return;

    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function gtag() {
      window.dataLayer?.push(arguments);
    };
    window.gtag('js', new Date());
    window.gtag('config', GTAG_AW_ID);

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GTAG_AW_ID}`;
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    if (!GTM_ID || !window.dataLayer || page === undefined) return;
    if (lastPage.current === page) return;
    lastPage.current = page;

    window.dataLayer.push({
      event: 'page_view',
      page_title: page,
      page_location: window.location.href
    });
  }, [page]);

  return null;
};

export default GoogleAnalytics;
